import type { EmailType, EmailInputFields, EmailValidationMessages } from './Emails.types';
import type { PersonFragment } from '@graphql/fragments/person.graphql.generated';
import type { GetTranslation } from '@shared/core/i18n';
import type { TranslationsByNamespace } from '@locales/generated';
import { MESSAGE_MIN_CHARACTER_LIMIT } from '@shared/constants/messaging';

import * as Yup from 'yup';

export const notEmailOrUrlRegex =
  /^((?!([-a-za-z0-9@:%_\+~#=]{2,256}\.(?!\.)[a-z]{2,6}\b([-a-za-z0-9@:%_\+.~#?&//=]*)|(([^<>()\[\]\\.,;:\s@"]+(\.(?!\.)[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.(?!\.)[0-9]{1,3}\.(?!\.)[0-9]{1,3}\.(?!\.)[0-9]{1,3}])|(([a-za-z\-0-9]+\.(?!\.))+[a-za-z]{2,}))))[\s\S])*$/i;

const getBaseValidationSchema = (validationMessages: EmailValidationMessages) => {
  return {
    eventId: Yup.string(),
    subject: Yup.string().matches(notEmailOrUrlRegex, { message: validationMessages.invalidSubjectIsEmailOrUrlError }).required(validationMessages.emailSubjectErrorMessage),
    headline: Yup.string().matches(notEmailOrUrlRegex, { message: validationMessages.invalidHeadlineIsEmailOrUrlError }),
    salutation: Yup.string().matches(notEmailOrUrlRegex, { message: validationMessages.invalidSalutationIsEmailOrUrlError }),
    includeGuestNames: Yup.boolean(),
    replyToEmail: Yup.string().email().required(validationMessages.replyToErrorMessage),
    testName: Yup.string(),
    testEmail: Yup.string().email(),
    imageUrl: Yup.string().url(),
  };
};

/**
 * Returns email-type-specific validation schema fields.
 *
 * Note: Ecards include URL/email blocking in message fields for security,
 * while generic emails do not require this restriction. This is intentional
 * product behavior based on different use cases for each email type.
 */
const getSpecificValidationSchema = (emailType: EmailType, validationMessages: EmailValidationMessages) => {
  switch (emailType) {
    case 'ecard':
      // Ecards have stricter validation: block URLs/emails in user-editable fields for security
      return {
        includeEventDate: Yup.boolean(),
        includeAddToCalendarLink: Yup.boolean(),
        buttonText: Yup.string().matches(notEmailOrUrlRegex, { message: validationMessages.invalidButtonTextIsEmailOrUrlError }).required(validationMessages.buttonTextError),
        subheadline: Yup.string().matches(notEmailOrUrlRegex, { message: validationMessages.invalidSubheadlineIsEmailOrUrlError }),
        message: Yup.string()
          .matches(notEmailOrUrlRegex, { message: validationMessages.invalidMessageIsEmailOrUrlError })
          .required(validationMessages.messageMinLengthError)
          .min(MESSAGE_MIN_CHARACTER_LIMIT, validationMessages.messageMinLengthError),
      };
    case 'generic':
    default:
      // Generic emails and any unknown types use standard validation without URL/email restrictions
      return {
        draftId: Yup.string(),
        includeWebsiteDetails: Yup.boolean(),
        includeAppInstructions: Yup.boolean(),
        includeEventPassword: Yup.boolean(),
        message: Yup.string().required(validationMessages.messageMinLengthError).min(MESSAGE_MIN_CHARACTER_LIMIT, validationMessages.messageMinLengthError),
        // post office v2 future functionality
        // selectedLink: Yup.mixed<GeneralMessageLinkType>()
      };
  }
};

export const getFormikValidationSchema = (emailType: EmailType, validationMessages: EmailValidationMessages) => {
  return Yup.object<EmailInputFields>({
    ...getBaseValidationSchema(validationMessages),
    ...getSpecificValidationSchema(emailType, validationMessages),
  });
};

export const EmailsAndEcardsFormControlOverrides = {
  Hint: {
    props: { display: 'none' },
  },
};

export const getFooterTextWithCouplesNames = (nameA: Maybe<string>, nameB: Maybe<string>, email: Maybe<string>, t: GetTranslation<TranslationsByNamespace['emailsAndEcards']>) => {
  let names = '';

  if (nameA) {
    names = nameA;
  }

  if (!!nameA && !!nameB) {
    names = `${nameA} and ${nameB}`;
  }

  const sentFromText = t('emailEditor', 'footerText', 'sentFrom')({ names });
  const emailPart = email ? t('emailEditor', 'footerText', 'sendRepliesTo')({ email }) : '';

  const sentFromTextWithEmail = `${sentFromText} ${emailPart}`;
  const sentFromTextWithoutEmail = `${sentFromText} `;

  if (email) {
    return sentFromTextWithEmail;
  }

  return sentFromTextWithoutEmail;
};

export const getPeopleIds = (selectedPeople: ReadonlyArray<Partial<PersonFragment>>) => {
  if (selectedPeople.length > 0) {
    return selectedPeople.filter(selectedPerson => selectedPerson.id).map(selectedPerson => selectedPerson.id!);
  }
  return [];
};
